// Live cursor coordinate readout (top-left of the globe) + right-click to copy.
//
// Shows where the mouse is pointing in lon/lat and MGA grid coords, plus the height of
// whatever is under the cursor. Right-click opens a small menu to copy any of those to the
// clipboard.
//
// Two constraints shape this file:
//
//  1. The viewer runs with requestRenderMode: true (see cesium/viewer.ts) - it renders only
//     when something changes. This readout is pure DOM and MUST NOT call requestRender(),
//     or mouse movement alone would drive the render loop and undo the optimisation that
//     keeps the GPU from pegging.
//  2. scene.pickPosition() is a GPU depth-buffer read. Running it on every raw mouse-move
//     event is far too expensive, so updates are throttled.
//
// It does not own a ScreenSpaceEventHandler. main.ts already has one for hover-picking and
// drives this via update() / hide() / openMenu(), so there is a single owner for mouse
// input on the canvas.
import {
  Cartographic,
  Math as CMath,
  type Cartesian2,
  type Cartesian3,
  type Viewer,
} from "cesium";

import { formatMetres, toMGA } from "../cesium/mga";
import { $mgaZone } from "../store";
import { toast } from "./toast";

const THROTTLE_MS = 60;

interface Fix {
  lon: number;
  lat: number;
  height: number | null;
}

export class CoordReadout {
  private el: HTMLElement;
  private menu: HTMLElement | null = null;
  private last = 0;
  private fix: Fix | null = null;

  constructor(private viewer: Viewer, container: HTMLElement) {
    this.el = document.createElement("div");
    this.el.className = "coord-readout";
    this.el.style.display = "none";
    container.append(this.el);

    // Re-render on zone change so the grid coords switch immediately instead of waiting
    // for the next mouse move.
    $mgaZone.subscribe(() => this.render());

    // Keep the browser's own context menu off the map - it would cover the copy menu.
    // Cesium generally suppresses this already; doing it explicitly costs nothing and
    // removes the dependency on that behaviour.
    this.el.addEventListener("contextmenu", (e) => e.preventDefault());
    viewer.canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  /** Called from main.ts on MOUSE_MOVE. Throttled internally. */
  update(screen: Cartesian2): void {
    const now = performance.now();
    if (now - this.last < THROTTLE_MS) return;
    this.last = now;

    const fix = this.resolve(screen);
    if (!fix) {
      this.hide();
      return;
    }
    this.fix = fix;
    this.render();
  }

  hide(): void {
    this.el.style.display = "none";
  }

  /**
   * Resolve a screen position to lon/lat/height. Height uses the same ladder as
   * cesium/markup.ts: the true picked surface first (which includes 3D models), then the
   * terrain, then nothing. Never fabricates a height.
   */
  private resolve(screen: Cartesian2): Fix | null {
    const scene = this.viewer.scene;

    let carto: Cartographic | null = null;
    let height: number | null = null;

    if (scene.pickPositionSupported) {
      const picked: Cartesian3 | undefined = scene.pickPosition(screen);
      if (picked) {
        carto = Cartographic.fromCartesian(picked) ?? null;
        if (carto) height = carto.height;
      }
    }

    if (!carto) {
      // Nothing under the cursor to depth-test against (empty sky, or an unsupported
      // depth buffer): fall back to where the view ray meets the ellipsoid.
      const onGlobe = this.viewer.camera.pickEllipsoid(screen);
      if (!onGlobe) return null;
      carto = Cartographic.fromCartesian(onGlobe) ?? null;
      if (!carto) return null;
      const terrain = scene.globe.getHeight(carto);
      height = terrain ?? null;
    }

    return {
      lon: CMath.toDegrees(carto.longitude),
      lat: CMath.toDegrees(carto.latitude),
      height,
    };
  }

  private render(): void {
    const fix = this.fix;
    if (!fix) return;
    const zone = $mgaZone.get();
    const mga = toMGA(fix.lon, fix.lat, zone);

    this.el.innerHTML = "";
    this.el.append(
      row("", `${fix.lat.toFixed(5)}°, ${fix.lon.toFixed(5)}°`),
      row(`MGA${zone}`, `${formatMetres(mga.easting)} E   ${formatMetres(mga.northing)} N`),
      row("Height", fix.height == null ? "—" : `${fix.height.toFixed(1)} m`),
    );
    this.el.style.display = "block";
  }

  // ---- right-click copy -------------------------------------------------------------

  /** Called from main.ts on RIGHT_CLICK. Opens the copy menu at the cursor. */
  openMenu(screen: Cartesian2): void {
    this.closeMenu();
    const fix = this.resolve(screen);
    if (!fix) return;
    this.fix = fix;
    this.render();

    const zone = $mgaZone.get();
    const mga = toMGA(fix.lon, fix.lat, zone);
    const latLon = `${fix.lat.toFixed(6)}, ${fix.lon.toFixed(6)}`;
    const grid = `${Math.round(mga.easting)} ${Math.round(mga.northing)}`;
    const h = fix.height == null ? null : `${fix.height.toFixed(2)} m`;

    const items: [string, string][] = [
      ["Copy lat, long", latLon],
      ["Copy lat, long + height", h ? `${latLon}, ${h}` : latLon],
      [`Copy MGA${zone} E N`, grid],
      [
        "Copy all",
        `Lat/Long: ${latLon}\nMGA${zone}: ${grid}\nHeight: ${h ?? "unavailable"}`,
      ],
    ];

    const menu = document.createElement("div");
    menu.className = "coord-menu";
    for (const [label, value] of items) {
      const btn = document.createElement("button");
      btn.className = "coord-menu-item";
      btn.textContent = label;
      btn.addEventListener("click", () => {
        void this.copy(value, label);
        this.closeMenu();
      });
      menu.append(btn);
    }

    const rect = this.viewer.canvas.getBoundingClientRect();
    menu.style.left = `${rect.left + screen.x + 4}px`;
    menu.style.top = `${rect.top + screen.y + 4}px`;
    document.body.append(menu);
    this.menu = menu;

    // Close on the next click OUTSIDE the menu, or on Escape.
    //
    // The outside-click check is load-bearing: pointerdown fires before click, so a
    // dismiss handler that closed unconditionally tore the menu out of the DOM before the
    // item's own click listener could run - every item silently copied nothing. Not
    // `{once: true}` either, or an ignored inside-click would consume the listener and
    // leave the menu permanently un-dismissable.
    setTimeout(() => {
      window.addEventListener("pointerdown", this.onDismiss);
      window.addEventListener("keydown", this.onKey);
    }, 0);
  }

  private onDismiss = (e: Event): void => {
    if (this.menu && e.target instanceof Node && this.menu.contains(e.target)) return;
    this.closeMenu();
  };
  private onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") this.closeMenu();
  };

  closeMenu(): void {
    this.menu?.remove();
    this.menu = null;
    window.removeEventListener("pointerdown", this.onDismiss);
    window.removeEventListener("keydown", this.onKey);
  }

  /**
   * navigator.clipboard needs a secure context - fine on https://geaar.app and on
   * localhost, but not on a plain-http LAN address, so keep the legacy fallback.
   */
  private async copy(text: string, label: string): Promise<void> {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        legacyCopy(text);
      }
      toast(`${label.replace(/^Copy /, "Copied ")}: ${firstLine(text)}`, "ok");
    } catch {
      try {
        legacyCopy(text);
        toast(`${label.replace(/^Copy /, "Copied ")}: ${firstLine(text)}`, "ok");
      } catch {
        toast("Could not copy to the clipboard", "warn");
      }
    }
  }
}

function row(label: string, value: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "coord-row";
  if (label) {
    const l = document.createElement("span");
    l.className = "coord-label";
    l.textContent = label;
    el.append(l);
  }
  const v = document.createElement("span");
  v.className = "coord-value";
  v.textContent = value;
  el.append(v);
  return el;
}

function firstLine(s: string): string {
  const [head] = s.split("\n");
  return head.length > 44 ? `${head.slice(0, 42)}…` : head;
}

function legacyCopy(text: string): void {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.append(ta);
  ta.select();
  const ok = document.execCommand("copy");
  ta.remove();
  if (!ok) throw new Error("execCommand copy failed");
}
